/**
 * Pressure system — per-cat sustained load tracking with low-pass filter.
 *
 * Per docs/PLAN.md §4.9 (Pressure Awareness):
 *   - loadAbove is the kg-equivalent of all bodies transitively resting on
 *     this cat, summed once per fixed step from the contact graph.
 *   - It's smoothed with a one-pole low-pass filter (τ ≈ 200 ms) so
 *     momentary contact spikes don't make the squish jitter.
 *   - pressureRatio = clamp(loadSmoothed / loadCapacity, 0..1.5).
 *
 * Pure-TS, no DOM, no physics imports. Tested in isolation.
 */

import type { CatArchetype, CatState } from './types.js';

/** One-pole low-pass coefficient for a fixed timestep. */
const computeAlpha = (dtSeconds: number, tauSeconds: number): number => {
  if (tauSeconds <= 0) return 1;
  return 1 - Math.exp(-dtSeconds / tauSeconds);
};

const PRESSURE_TAU_SECONDS = 0.2;
const PRESSURE_RATIO_MAX = 1.5;

/**
 * Apply the per-frame pressure update to a single cat.
 *
 * @param state Current cat state (mutated in place — the smoothed load and
 *              pressureRatio fields are updated).
 * @param archetype This cat's archetype (provides `loadCapacityKg`).
 * @param rawLoadAboveKg Instantaneous load above this cat, summed by sim
 *                      from the contact graph this step.
 * @param dtSeconds Fixed timestep length in seconds.
 */
export function updateCatPressure(
  state: CatState,
  archetype: CatArchetype,
  rawLoadAboveKg: number,
  dtSeconds: number,
): void {
  const alpha = computeAlpha(dtSeconds, PRESSURE_TAU_SECONDS);
  // One-pole low-pass: smoothed += alpha * (raw - smoothed)
  const smoothed = state.loadAboveKg + alpha * (rawLoadAboveKg - state.loadAboveKg);
  state.loadAboveKg = smoothed;

  const ratio = archetype.loadCapacityKg > 0 ? smoothed / archetype.loadCapacityKg : 0;
  state.pressureRatio = Math.max(0, Math.min(PRESSURE_RATIO_MAX, ratio));
}

/**
 * Visual squish levels per docs/PLAN.md §4.9 visual reactions table.
 * Render layer maps these to sprite vertical scales.
 */
export type SquishLevel = 'idle' | 'subtle' | 'pronounced' | 'overload';

export function squishLevelFromRatio(ratio: number): SquishLevel {
  if (ratio < 0.4) return 'idle';
  if (ratio < 0.8) return 'subtle';
  if (ratio <= 1.0) return 'pronounced';
  return 'overload';
}

/** Vertical scale factor for the greybox sprite. Cosmetic only. */
export function squishScaleFromRatio(ratio: number): number {
  // Linear ramp from 1.0 at 0 to 0.78 at 1.5 (overloaded).
  // Bounded so we don't squish a cat into a pancake.
  const minScale = 0.78;
  const maxScale = 1.0;
  const t = Math.max(0, Math.min(1, ratio / 1.5));
  return maxScale + (minScale - maxScale) * t;
}

/** Whether the bug-eyes overlay should be visible at this pressure. */
export function bugEyesActive(ratio: number): boolean {
  return ratio >= 0.8;
}
